import * as React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ShowFnOutput } from 'mui-modal-provider';

import { venueUpdateSubscriptionApi } from '../../../../api/stripe/venueUpdateSubscriptionApi';
import { OfferCategory, VenueSubscriptionItem } from '../../../../graphql/API';
import { useVenueGetSubscriptionUpgradeCostQuery } from '../../../../hooks/reactQuery/stripe/useVenueGetSubscriptionUpgradeCostQuery';
import { toCurrencyAmount } from '../../../../pages/Credit/components/PremiumSubscriptionSection/helper';
import {
  getBrandCycleOptions,
  getCycleOptions,
  getPlanPriceBySku,
  toDecimal,
} from '../../../../pages/SubscriptionPlans/common/helper';
import { extractSubscriptionPlan } from '../../../../pages/SubscriptionPlans/common/helper';
import PlanPrice from '../../../../pages/SubscriptionPlans/components/Plans/PlanPrice';
import { Plan, SubscriptionConditionKind } from '../../../../pages/SubscriptionPlans/types';
import { StripePriceWithMetadata } from '../../../../redux/features/subscription/types';
import { useAppSelector } from '../../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../../utils/venue-tags';
import Button from '../../../Button/Button';
import LoadingButton from '../../../Button/LoadingButton';
import { BaseDialogProps } from '../../Base';
import CycleOptions from './CycleOptions/CycleOptions';

export type CycleChangeProps = {
  price: StripePriceWithMetadata | undefined;
  subscription: VenueSubscriptionItem | undefined;
  subscriptionKind: SubscriptionConditionKind;
  modalRef: React.MutableRefObject<ShowFnOutput<BaseDialogProps> | undefined>;
};

const mapLabel: Record<Plan, { title: string }> = {
  basic: { title: 'Free' },
  starter: { title: 'Starter' },
  plus: { title: 'PLUS' },
};

const mapSubscriptionKind: Partial<
  Record<
    SubscriptionConditionKind,
    { description: (p: number, pc?: string) => string; buttonLabel: (p: string) => string }
  >
> = {
  'plan-upgrade': {
    buttonLabel: (p) => `Upgrade to ${p}`,
    description: (p, pc) => {
      if (p > 0) {
        return `You can take advantage of the new plan benefits by paying the difference of ${pc} based on your current plan`;
      }
      return `You can take advantage of the new plan benefits. The difference in fare will be added to your credit balance and included in your next invoice`;
    },
  },
  'plan-downgrade': {
    buttonLabel: (p) => `Downgrade to ${p}`,
    description: () =>
      'You can take advantage of the new plan benefits. The difference in fare will be added to your credit balance and included in your next invoice',
  },
};

const calculateDiff = (newPrice: number, currency: string) =>
  toCurrencyAmount(toDecimal(String(newPrice)), currency);

const toCycleMonths = (productSku: string | undefined, fallbackCycle: string | undefined) => {
  const cycleFromSku = productSku?.split('_')[2];
  const cycle = cycleFromSku ?? fallbackCycle;
  const months = Number(cycle);
  return Number.isFinite(months) && months > 0 ? months : 1;
};

const formatBillingDateLabel = (date: string | null | undefined) => {
  if (!date) return '';
  const d = dayjs(date);
  const withYear = d.year() !== dayjs().year();
  return d.format(withYear ? 'MMMM DD, YYYY' : 'MMMM DD');
};

const UpdatePlan: React.FC<CycleChangeProps> = ({
  price,
  subscription,
  subscriptionKind,
  modalRef,
}) => {

  const [newPrice, setNewPrice] = React.useState(price);
  const { pricing } = useAppSelector((state) => state.subscription.value);
  const { tags: venueTags } = useAppSelector((state) => state.venue.value) || {};

  const [plan, , cycle] = extractSubscriptionPlan(price?.metadata.ProductSku ?? '');

  const {
    data: subscriptionCostData,
    isFetching,
    isLoading,
  } = useVenueGetSubscriptionUpgradeCostQuery({
    newPriceID: newPrice?.id,
    subscriptionID: String(subscription?.subscriptionId),
  });

  console.log({price, subscriptionKind, subscriptionCostData, newPrice, subscription});
  console.log({newPrice: newPrice?.id, subscriptionID: String(subscription?.subscriptionId)});

  const mutation = useMutation({
    mutationFn: venueUpdateSubscriptionApi,
    onSuccess: () => {
      window.location.href = `/plans?message_type=${subscriptionKind}&updatedPlan=${plan}`;
    },
  });

  const isExistingOldSubscriber = React.useMemo(() => {
    return subscription?.sku && !subscription.sku.endsWith('_v3');
  }, [subscription]);

  const options = React.useMemo(
    () =>
      venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans)
        ? getBrandCycleOptions(plan, OfferCategory.brand, pricing ?? [], !isExistingOldSubscriber)
        : getCycleOptions(plan, pricing ?? []),
    [plan, pricing, venueTags, isExistingOldSubscriber],
  );
  const ms = React.useMemo(() => mapSubscriptionKind[subscriptionKind], [subscriptionKind]);

  const priceDiff = React.useMemo(
    () => (subscriptionCostData?.amountDue || 0) / 100 - Number((subscription?.price ?? 0) * 100),
    [subscription?.price, subscriptionCostData?.amountDue],
  );

  const priceDiffCurrency = React.useMemo(
    () => calculateDiff(priceDiff, subscriptionCostData?.currency ?? ''),
    [priceDiff, subscriptionCostData?.currency],
  );

  const optionsChangeHandler = React.useCallback(
    (c: string) => {
      const sku = venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans)
        ? `${plan}_brand_${c}`
        : `${plan}_venue_${c}`;

      const np = getPlanPriceBySku(sku, pricing ?? []);

      setNewPrice(np);
    },
    [plan, pricing, venueTags],
  );


  const nextBillingDate = React.useMemo(() => {
    if (!subscription?.expirationDate) return undefined;
    const months = toCycleMonths(newPrice?.metadata.ProductSku, cycle);
    if (months === 1) return subscription.expirationDate ?? undefined;
    return dayjs(subscription.expirationDate).add(months, 'month').toISOString();
  }, [cycle, newPrice?.metadata.ProductSku, subscription?.expirationDate]);

  return (
    <Box>
      <Box>
        <Typography sx={{ marginBottom: 2 }}>Select a billing period:</Typography>
        <CycleOptions options={options} cycle={cycle} onChange={optionsChangeHandler} />
      </Box>

      <Box sx={{ marginTop: 3, marginBottom: 5 }}>
        <Typography fontWeight='medium'>
          {subscriptionKind === 'plan-upgrade' ? 'Pro-Rated Price:' : 'New Price:'}
        </Typography>
        <PlanPrice
          showCostUpgrade={subscriptionKind !== 'plan-downgrade'}
          price={newPrice}
          highlightPrice='#37BB90'
          showBasePrice={false}
          boldBilledPrice={false}
          isLoading={isLoading || isFetching}
          costUpgrade={subscriptionCostData}
          subscription={subscription}
          nextBillingDate={nextBillingDate}
        />
      </Box>

      {!isLoading && !isFetching && subscriptionKind === 'plan-upgrade' ? (
        <>
          <Typography sx={{ fontWeight: '700' }}>Description</Typography>

          <Typography sx={{ fontSize: '15px' }}>
            You will pay a pro-rated amount of{' '}
            {toCurrencyAmount(
              (subscriptionCostData?.amountDue || 0) / 100,
              subscriptionCostData?.currency ?? '',
            )}{' '}
            today for the rest of this billing period (until{' '}
            {formatBillingDateLabel(nextBillingDate ?? subscription?.expirationDate)}). After{' '}
            {formatBillingDateLabel(nextBillingDate ?? subscription?.expirationDate)}, your subscription renews
            monthly at{' '}
            {toCurrencyAmount(toDecimal(newPrice?.metadata.CostPerMonth), newPrice?.currency ?? '')}
            .
          </Typography>
        </>
      ) : !isLoading && !isFetching ? (
        <>
          <Typography sx={{ fontWeight: '700' }}>Difference</Typography>

          <Typography>{ms?.description(priceDiff, priceDiffCurrency)}</Typography>
        </>
      ) : null}

      <Stack spacing={2} direction='row' sx={{ mt: 6, width: '100%' }}>
        <Button
          size='large'
          variant='outlined'
          sx={{ px: 10 }}
          onClick={() => modalRef.current?.destroy()}
        >
          Cancel
        </Button>
        <LoadingButton
          size='large'
          variant='contained'
          loading={mutation.isPending}
          fullWidth
          onClick={() => {
            mutation.mutate({
              newPriceId: newPrice?.id,
              subscriptionId: subscription?.subscriptionId,
            });
          }}
          disabled={isLoading || isFetching}
        >
          {ms?.buttonLabel(mapLabel[plan].title)}
        </LoadingButton>
      </Stack>
    </Box>
  );
};

export default UpdatePlan;
